import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

const HAS_IMAGE_FILTER_PARAM = 'filter.p.tag';
const HAS_IMAGE_FILTER_VALUE = 'has-image';
const ADOPTED_TITLE_FILTER_VALUE = 'adoptedtitle';

function ensureHasImageFilter(searchParams) {
  const hasHasImage = searchParams.getAll(HAS_IMAGE_FILTER_PARAM).includes(HAS_IMAGE_FILTER_VALUE);

  if (!hasHasImage) {
    searchParams.append(HAS_IMAGE_FILTER_PARAM, HAS_IMAGE_FILTER_VALUE);
  }
}

function ensureAdoptedTitleFilter(searchParams) {
  const hasAdoptedTitle = searchParams.getAll(HAS_IMAGE_FILTER_PARAM).includes(ADOPTED_TITLE_FILTER_VALUE);

  if (!hasAdoptedTitle) {
    searchParams.append(HAS_IMAGE_FILTER_PARAM, ADOPTED_TITLE_FILTER_VALUE);
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {HTMLElement | null} Insert-after target when moving dropdown back from body (portal) */
  #dropdownPortalParent = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    // Add form submit handler for ISBN searches
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.#handleFormSubmit, { signal });
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      // Inline header search: use data-desktop-search-open so layout change doesn't close panel on first click
      this.#bindInlineSearchOpenState(signal);
    }
  }

  /**
   * Returns true if the given node is inside this component (including shadow DOM).
   * @param {Node | null} node
   * @returns {boolean}
   */
  #isFocusInsideComponent(node) {
    if (!node) return false;
    if (this.#dropdownPortalParent) {
      const portalWrapper = document.body.querySelector(`.predictive-search-form__content-wrapper.${PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS}`);
      if (portalWrapper && portalWrapper.contains(node)) return true;
    }
    let current = node;
    while (current) {
      if (current === this) return true;
      current = current.parentNode || (current instanceof ShadowRoot ? current.host : null);
    }
    return false;
  }

  static DROPDOWN_PORTAL_CLASS = 'predictive-search-dropdown--portal';

  #moveDropdownToBody() {
    const wrapper = this.querySelector('.predictive-search-form__content-wrapper');
    const form = this.refs.form;
    if (!wrapper || !form || wrapper.classList.contains(PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS)) return;
    const header = form.querySelector('.predictive-search-form__header');
    if (!header) return;
    this.#dropdownPortalParent = header;
    wrapper.classList.add(PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS);
    document.body.appendChild(wrapper);
  }

  #moveDropdownBack() {
    const wrapper = document.body.querySelector(`.predictive-search-form__content-wrapper.${PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS}`);
    if (!wrapper || !this.#dropdownPortalParent) return;
    wrapper.classList.remove(PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS);
    this.#dropdownPortalParent.insertAdjacentElement('afterend', wrapper);
    this.#dropdownPortalParent = null;
  }

  /**
   * Binds open state for inline header search using data-desktop-search-open
   * so layout change on expand doesn't trigger blur and close immediately.
   * Dropdown top uses body’s existing --header-height only (no extra JS or vars).
   * @param {AbortSignal} signal
   */
  #bindInlineSearchOpenState(signal) {
    let closeTimeoutId = 0;
    let openedAt = 0;
    const DESKTOP_OPEN_ATTR = 'data-desktop-search-open';
    const BLUR_CLOSE_DELAY_MS = 180;
    const OPEN_CLICK_IGNORE_MS = 150;

    const closePanel = () => {
      if (this.#dropdownPortalParent) this.#moveDropdownBack();
      this.removeAttribute(DESKTOP_OPEN_ATTR);
      if (this.refs.searchInput) this.refs.searchInput.blur();
    };

    const scheduleCloseIfFocusLeft = () => {
      closeTimeoutId = window.setTimeout(() => {
        closeTimeoutId = 0;
        if (!this.#isFocusInsideComponent(document.activeElement)) {
          closePanel();
        }
      }, BLUR_CLOSE_DELAY_MS);
    };

    const isDesktopViewport = () => window.matchMedia('(min-width: 1201px)').matches;

    this.refs.searchInput.addEventListener(
      'focus',
      () => {
        if (closeTimeoutId) {
          clearTimeout(closeTimeoutId);
          closeTimeoutId = 0;
        }
        /* Do not fetch empty state on focus: Liquid already renders Popular Searches + Featured Products
           in predictive-search-empty-state. Replacing that with API HTML caused stutter/flash in Safari. */
        if (isDesktopViewport()) {
          this.setAttribute(DESKTOP_OPEN_ATTR, 'true');
          openedAt = Date.now();
          requestAnimationFrame(() => this.#moveDropdownToBody());
        } else {
          this.removeAttribute(DESKTOP_OPEN_ATTR);
          requestAnimationFrame(() => {
            const wrapper = this.querySelector('.predictive-search-form__content-wrapper');
            if (wrapper) wrapper.style.top = '';
          });
        }
      },
      { signal }
    );

    this.refs.searchInput.addEventListener('blur', () => scheduleCloseIfFocusLeft(), { signal });

    document.addEventListener(
      'click',
      (e) => {
        if (openedAt && Date.now() - openedAt < OPEN_CLICK_IGNORE_MS) return;
        const clickedInside = e.composedPath().includes(this);
        if (!clickedInside) {
          scheduleCloseIfFocusLeft();
        }
      },
      { signal }
    );

    const closeButton = this.refs.closeModalButton ?? this.querySelector('.predictive-search__close-modal-button');
    if (closeButton) {
      closeButton.addEventListener(
        'click',
        () => {
          if (closeTimeoutId) clearTimeout(closeTimeoutId);
          closeTimeoutId = 0;
          closePanel();
        },
        { signal }
      );
    }
  }

  /**
   * Handle form submission - redirect to ISBN search if applicable
   * @param {Event} event - The form submit event
   */
  #handleFormSubmit = (event) => {
    const searchTerm = this.refs.searchInput.value.trim();

    if (this.#isISBN(searchTerm)) {
      event.preventDefault();
      const searchUrl = new URL('/search', location.origin);
      searchUrl.searchParams.set(
        'filter.p.m.course_materials.isbn_ean',
        searchTerm
      );
      searchUrl.searchParams.set('q', '*');
      window.location.href = searchUrl.toString();
    }
    // Otherwise, let the form submit naturally to routes.search_url
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Check if search term is an ISBN (10 or 13 digits)
   * @param {string} searchTerm - The search term to check
   * @returns {boolean} True if ISBN-10 or ISBN-13
   */
  #isISBN(searchTerm) {
    const trimmed = searchTerm.trim();
    return /^\d{10}$/.test(trimmed) || /^\d{13}$/.test(trimmed);
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const searchTerm = this.refs.searchInput.value.trim();

        // If ISBN, always navigate to search results page with ISBN filter
        if (this.#isISBN(searchTerm)) {
          event.preventDefault();
          const searchUrl = new URL('/search', location.origin);
          searchUrl.searchParams.set(
            'filter.p.m.course_materials.isbn_ean',
            searchTerm
          );
          searchUrl.searchParams.set('q', '*');
          searchUrl.searchParams.set('filter.v.availability', '1');
          window.location.href = searchUrl.toString();
          return;
        }

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        }
        // Otherwise, let the form submit naturally
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Handles the View All button click.
   * Navigates to the full search results page with the current search term and availability filter.
   */
  handleViewAllClick = () => {
    const searchTerm = this.refs.searchInput.value.trim();
    const searchUrl = new URL(Theme.routes.search_url, location.origin);

    // If ISBN, always navigate to search results page with ISBN filter
    if (this.#isISBN(searchTerm)) {
      searchUrl.searchParams.set(
        'filter.p.m.course_materials.isbn_ean',
        searchTerm
      );
      searchUrl.searchParams.set('q', '*');
    } else {
      if (searchTerm.length > 0) {
        searchUrl.searchParams.set('q', searchTerm);
      }
      searchUrl.searchParams.set('filter.v.availability', '1');
      ensureHasImageFilter(searchUrl.searchParams);
      ensureAdoptedTitleFilter(searchUrl.searchParams);
    }
    
    window.location.href = searchUrl.toString();
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    // For ISBN, fetch real search results using metafield filter
    if (this.#isISBN(searchTerm)) {
      this.#getISBNSearchResults(searchTerm);
      return;
    }

    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Ensure availability filter is always set to "In stock" (value "1")
    url.searchParams.set('filter.v.availability', '1');
    ensureHasImageFilter(url.searchParams);
    ensureAdoptedTitleFilter(url.searchParams);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch ISBN search results using the regular search API with metafield filter.
   * Uses Section Rendering API to get results that can be rendered in the dropdown.
   * @param {string} isbn - The ISBN to search for (10 or 13 digits)
   */
  async #getISBNSearchResults(isbn) {
    if (!this.dataset.sectionId) return;

    // Use regular search URL (not predictive search) because metafield filters
    // only work with /search, not /search/suggest
    const url = new URL(Theme.routes.search_url, location.origin);

    // Use wildcard query with ISBN metafield filter
    url.searchParams.set('q', '*');
    url.searchParams.set('filter.p.m.course_materials.isbn_ean', isbn);
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('type', 'product');
    //ensureHasImageFilter(url.searchParams);
    ensureAdoptedTitleFilter(url.searchParams);

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId,
        false,
        url
      );

      if (!resultsMarkup || abortController.signal.aborted) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      // On error, fall back to the empty ISBN dropdown
      this.#showISBNDropdown();
      throw error;
    }
  }

  /**
   * Show empty dropdown for ISBN searches (used as fallback on error)
   */
  #showISBNDropdown() {
    const { predictiveSearchResults } = this.refs;

    const isbnDropdownMarkup = `
      <div
        id="predictive-search-results"
        class="predictive-search-dropdown"
        role="listbox"
        aria-label="Search results"
        aria-expanded="true"
        data-transition-container
      >
        <div class="predictive-search-results__inner" data-search-results>
          <div class="visually-hidden" role="status" aria-live="polite">
            Searching for ISBN...
          </div>
        </div>
      </div>
    `;

    morph(predictiveSearchResults, isbnDropdownMarkup);
    this.#resetScrollPositions();
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    // Ensure availability filter is always set to "In stock" (value "1")
    url.searchParams.set('filter.v.availability', '1');
    ensureHasImageFilter(url.searchParams);
    ensureAdoptedTitleFilter(url.searchParams);

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    this.#currentIndex = -1;
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    await this.#loadEmptyStateContent();
  };

  /**
   * Load empty state (Popular Searches + Featured Products) into results container.
   * Used on reset and when focusing inline search with empty input.
   */
  #loadEmptyStateContent = async () => {
    const { predictiveSearchResults } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();

      if (recentlyViewedMarkup) {
        if (abortController.signal.aborted) return;

        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');

        if (recentlyViewedProductsHtml) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }

          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

          if (collectionElement) {
            collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
          }
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}